import { computed, ref, inject, provide, type ComputedRef, type InjectionKey, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '@stores/useSearchStore';
import { useMobile } from '@composables/useMobile';
import { CountryFilterMapper } from '@app/routing/mappers/CountryFilterMapper';
import { AreaFilterMapper } from '@app/routing/mappers/AreaFilterMapper';
import { ContinentFilterMapper } from '@app/routing/mappers/ContinentFilterMapper';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { SelectListItemStateType } from '@app/enums/SelectListItemStateType';
import type ISelectItem from '@app/interfaces/dtos/ISelectItem';
import type { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { PortalType } from '@studyportals/domain-client';
import { areasAll } from '@studyportals/static-domain-data/areas/areas';
import { countriesExtendedAll } from '@studyportals/static-domain-data/countries/countries-extended';
import type { ItemEntry } from '@app/interfaces/useMultiselect.interface';
import { countriesForContinent } from '@studyportals/static-domain-data/continents/continent-iso-country-mapping';
import IExtendedSearchQueryFilter from '@app/interfaces/dtos/IExtendedSearchQueryFilter';
import { IExtendedFilterValue } from '@app/interfaces/dtos/IExtendedFilterValue';
import { ModalStateFooter } from '@app/interfaces/modalState.interface';
import { useMobileModal } from './useMobileModal';
import { IArea } from '@studyportals/static-domain-data/areas/i-area';
import { IFacetKeyValuePair } from '@domain/interfaces/dtos/IFacetKeyValuePair';
import { useRollbar } from '@composables/useRollbar';

type LocationFilterContext = {
	isMobile: Ref<boolean>;
	isContinentOpen: Ref<boolean>;
	showContinentOptions: Ref<boolean>;
	continentItems: ComputedRef<ISelectItem[]>;
	selectedContinentName: ComputedRef<string | null>;
	selectedIds: ComputedRef<(string | number)[]>;
	topCountriesForSelectedContinent: ComputedRef<ItemEntry[]>;
	multiselectOptionsForSelectedContinent: ComputedRef<IMultiselectOption[]>;
	footer: ComputedRef<ModalStateFooter>;
	toggleContinent: () => void;
	handleContinentChange: () => void;
	handleSelect: (value: string | number, selected: boolean) => Promise<void>;
};

type NextFiltersResult = {
	nextFilters: IExtendedSearchQueryFilter[];
	changed: boolean;
	closeContinentOptions: boolean;
};

const locationFilterContextKey: InjectionKey<LocationFilterContext> = Symbol('LocationFilterContext');
const topCountries = ['US', 'GB', 'AU', 'CA', 'DE'];

const createLocationFilterContext = (): LocationFilterContext => {
	const { logError } = useRollbar();
	const searchStore = useSearchStore();
	const { isMobile } = useMobile();
	const { close: closeMobileModal } = useMobileModal();

	const { filtersSelection, pageData, portalType } = storeToRefs(searchStore);

	const portalTypeValue = computed(() => portalType.value as PortalType);
	const countryMapper = CountryFilterMapper.getInstance();
	const areaMapper = AreaFilterMapper.getInstance();
	const continentMapper = ContinentFilterMapper.getInstance();

	const isContinentOpen = ref(false);
	const showContinentOptions = ref(false);

	const isoByCountryId = new Map(countriesExtendedAll.map((c) => [c.id, c.iso]));

	const buildAreaMappings = (): {
		areaById: Map<string, string>;
		areasByIso: Map<string, IArea[]>;
	} => {
		const areaByIdMap = new Map<string, string>();
		const areasByIsoMap = new Map<string, IArea[]>();

		for (const area of areasAll as IArea[]) {
			if (!area.countryId) continue;

			const iso = isoByCountryId.get(area.countryId);
			if (!iso) continue;

			const areaId = area.id.toString();

			// Populate areaById map
			areaByIdMap.set(areaId, iso);

			// Populate areasByIso map
			if (!areasByIsoMap.has(iso)) areasByIsoMap.set(iso, []);
			areasByIsoMap.get(iso)!.push({ id: areaId, name: area.name } as IArea);
		}

		return { areaById: areaByIdMap, areasByIso: areasByIsoMap };
	};

	const { areaById, areasByIso } = buildAreaMappings();

	const facetCounts = computed(() => {
		const facets = (pageData.value?.filtersFacets ?? {}) as Record<
			string,
			IFacetKeyValuePair | undefined
		>;

		for (const type of [FilterType.COUNTRY, FilterType.AREA, FilterType.CONTINENT]) {
			facets[type] = facets[type] || {};
		}

		return facets;
	});

	const selectedCountryIds = computed(() => new Set(
		filtersSelection.value.find((f) => f.type === FilterType.COUNTRY)?.values.map((v) => v.id) ?? []
	));

	const selectedAreaIds = computed(() => new Set(
		filtersSelection.value.find((f) => f.type === FilterType.AREA)?.values.map((v) => v.id) ?? []
	));

	const selectedContinent = computed(() =>
		filtersSelection.value.find((f) => f.type === FilterType.CONTINENT)?.values[0] ?? null
	);

	const selectedIds = computed<(string | number)[]>(() => {
		const ids = [
			...Array.from(selectedCountryIds.value).map((id) => `country_${id}`),
			...Array.from(selectedAreaIds.value).map((id) => `area_${id}`)
		];
		if (showContinentOptions.value && isMobile.value && selectedContinent.value?.id) {
			ids.push(`continent_${selectedContinent.value.id}`);
		}
		return ids;
	});

	const valueTypeMap = computed(() => {
		const map = new Map<string | number, FilterType>();
		countryMapper.getAllExtendedFilterValues(portalTypeValue.value).forEach((value) => {
			map.set(`country_${value.id}`, FilterType.COUNTRY);
		});
		(areaMapper.getAllExtendedFilterValues(portalTypeValue.value) ?? []).forEach((value) => {
			map.set(`area_${value.id}`, FilterType.AREA);
		});
		continentMapper.getAllExtendedFilterValues(portalTypeValue.value).forEach((value) => {
			map.set(`continent_${value.id}`, FilterType.CONTINENT);
		});
		return map;
	});

	const allCountries = computed(() => {
		return countryMapper.getAllExtendedFilterValues(portalTypeValue.value).map((country) => {
			const subAreas = areasByIso.get(country.id);
			const subItems: ISelectItem[] | undefined = subAreas?.map((area) => ({
				value: `area_${area.id}`,
				text: area.name,
				count: facetCounts.value[FilterType.AREA]?.[area.id] || 0,
				state: selectedAreaIds.value.has(area.id) ? SelectListItemStateType.SELECTED : SelectListItemStateType.ENABLED
			}));

			return {
				item: {
					value: `country_${country.id}`,
					text: country.name ?? country.id,
					count: facetCounts.value[FilterType.COUNTRY]?.[country.id] || 0,
					state: selectedCountryIds.value.has(country.id)
						? SelectListItemStateType.SELECTED
						: SelectListItemStateType.ENABLED
				} as ISelectItem,
				subItems
			} as ItemEntry;
		});
	});

	const multiselectOptions = computed<IMultiselectOption[]>(() => {
		const options: IMultiselectOption[] = [];
		for (const country of countryMapper.getAllExtendedFilterValues(portalTypeValue.value)) {
			options.push({
				value: `country_${country.id}`,
				label: country.name ?? country.id,
				count: facetCounts.value[FilterType.COUNTRY]?.[country.id] || 0
			});

			const subAreas = areasByIso.get(country.id);
			subAreas?.forEach((area) => {
				options.push({
					value: `area_${area.id}`,
					label: area.name,
					count: facetCounts.value[FilterType.AREA]?.[area.id] || 0
				});
			});
		}
		return options;
	});

	const continentItems = computed<ISelectItem[]>(() =>
		continentMapper.getAllExtendedFilterValues(portalTypeValue.value).map((continent) => ({
			value: `continent_${continent.id}`,
			text: continent.name ?? continent.id,
			count: facetCounts.value[FilterType.CONTINENT]?.[continent.id] || 0,
			state: selectedContinent.value?.id === continent.id
				? SelectListItemStateType.SELECTED
				: SelectListItemStateType.ENABLED
		}))
	);

	const listTopCountries = computed(() => topCountries
		.map((code) => allCountries.value.find((country) => String(country.item.value) === `country_${code}`))
		.filter((item): item is ItemEntry => !!item)
	);

	const continentTopCountries = computed(() => {
		if (!selectedContinent.value?.id) return listTopCountries.value;

		const continentIso = String(selectedContinent.value.id);
		const countryIsosForContinent = countriesForContinent(continentIso) || topCountries;

		return allCountries.value
			.filter((country) => {
				const countryId = String(country.item.value).replace(/^country_/, '');
				return countryIsosForContinent.includes(countryId);
			})
			.sort((a, b) => (b.item.count || 0) - (a.item.count || 0))
			.slice(0, 5);
	});

	const selectedCountriesForSelectedContinent = computed<ItemEntry[]>(() => {
		const selectedCountries = allCountries.value.filter(({ item }) => {
			const countryId = String(item.value).replace(/^country_/, '');
			return selectedCountryIds.value.has(countryId);
		});
		if (!selectedContinent.value?.id) return selectedCountries;

		const continentIso = String(selectedContinent.value.id);
		const countryIsosForContinent = new Set(countriesForContinent(continentIso) || topCountries);

		return selectedCountries.filter(({ item }) => {
			const countryId = String(item.value).replace(/^country_/, '');
			return countryIsosForContinent.has(countryId);
		});
	});

	const topCountriesForSelectedContinent = computed(() => {
		const base = continentTopCountries.value;
		const baseIds = new Set(base.map((entry) => String(entry.item.value)));

		const selectedExtras = selectedCountriesForSelectedContinent.value.filter(
			(entry) => !baseIds.has(String(entry.item.value))
		);

		return [...base, ...selectedExtras];
	});

	const multiselectOptionsForSelectedContinent = computed<IMultiselectOption[]>(() => {
		if (!selectedContinent.value?.id) return multiselectOptions.value;

		const continentIso = String(selectedContinent.value.id);
		const countryIsosForContinent = countriesForContinent(continentIso) || topCountries;

		return multiselectOptions.value.filter((option) => {
			const value = String(option.value);
			if (value.startsWith('country_')) {
				const countryId = value.replace(/^country_/, '');
				return countryIsosForContinent.includes(countryId);
			}
			if (value.startsWith('area_')) {
				const areaId = value.replace(/^area_/, '');
				const countryIso = areaById.get(areaId);
				return countryIso ? countryIsosForContinent.includes(countryIso) : false;
			}
			return false;
		});
	});

	const toggleContinent = (): void => {
		isContinentOpen.value = !isContinentOpen.value;
	};

	const handleContinentChange = (): void => {
		isContinentOpen.value = true;
		showContinentOptions.value = true;
	};

	const pruneFiltersForContinent = (filters: IExtendedSearchQueryFilter[], continentIso: string): IExtendedSearchQueryFilter[] => {
		const allowedCountryIsos = new Set(countriesForContinent(continentIso) || []);
		if (allowedCountryIsos.size === 0) return filters;

		return filters
			.map((filter) => {
				if (filter.type === FilterType.COUNTRY) {
					const values = filter.values.filter((v) => allowedCountryIsos.has(String(v.id)));
					return values.length ? { ...filter, values } : null;
				}

				if (filter.type === FilterType.AREA) {
					const values = filter.values.filter((v) => {
						const iso = areaById.get(String(v.id));
						return iso ? allowedCountryIsos.has(iso) : false;
					});
					return values.length ? { ...filter, values } : null;
				}

				return filter;
			})
			.filter((f): f is NonNullable<typeof f> => !!f);
	};

	const replaceFilterValues = (
		filters: IExtendedSearchQueryFilter[],
		type: FilterType,
		values: IExtendedSearchQueryFilter['values']
	): IExtendedSearchQueryFilter[] => {
		const current = filters.find((filter) => filter.type === type);
		if (!current) return [...filters, { type, values }];

		return filters.map((filter) => filter.type === type ? { ...current, values } : filter);
	};

	const addFilterValue = (
		filters: IExtendedSearchQueryFilter[],
		type: FilterType,
		value: IExtendedSearchQueryFilter['values'][number]
	): IExtendedSearchQueryFilter[] => {
		const current = filters.find((filter) => filter.type === type);
		if (!current) return [...filters, { type, values: [value] }];

		return filters.map((filter) => {
			if (filter.type !== type) return filter;
			return { ...current, values: [...current.values, value] };
		});
	};

	const removeFilterValue = (
		filters: IExtendedSearchQueryFilter[],
		type: FilterType,
		valueId: string
	): IExtendedSearchQueryFilter[] => {
		const current = filters.find((filter) => filter.type === type);
		if (!current) return filters;

		const remaining = current.values.filter((value) => value.id !== valueId);
		if (remaining.length === 0) {
			return filters.filter((filter) => filter.type !== type);
		}

		return filters.map((filter) => filter.type === type ? { ...current, values: remaining } : filter);
	};

	const removeAreasForCountry = (
		filters: IExtendedSearchQueryFilter[],
		countryIso: string
	): IExtendedSearchQueryFilter[] => {
		const current = filters.find((filter) => filter.type === FilterType.AREA);
		if (!current) return filters;

		const remaining = current.values.filter((value) => (
			areaById.get(String(value.id)) !== countryIso
		));

		if (remaining.length === 0) {
			return filters.filter((filter) => filter.type !== FilterType.AREA);
		}

		return filters.map((filter) => filter.type === FilterType.AREA
			? { ...current, values: remaining }
			: filter
		);
	};

	const ensureCountrySelection = (
		filters: IExtendedSearchQueryFilter[],
		areaId: string | number
	): IExtendedSearchQueryFilter[] => {
		const countryIso = areaById.get(String(areaId));
		const countryValue = countryIso
			? countryMapper.getExtendedFilterValueById(countryIso)
			: null;

		if (!countryValue) return filters;

		const countryFilter = filters.find((filter) => filter.type === FilterType.COUNTRY);
		const alreadyInCountry = countryFilter?.values.some((value) => value.id === countryValue.id);

		if (alreadyInCountry) return filters;

		return addFilterValue(filters, FilterType.COUNTRY, countryValue);
	};

	/**
	 * Computes the next filter state when a location filter is selected/deselected.
	 * Handles continent replacement, area parent-country selection, and filter pruning.
	 */
	const computeFilterUpdate = (
		type: FilterType,
		mappedValue: IExtendedFilterValue,
		selected: boolean
	): NextFiltersResult => {
		const currentSelection = filtersSelection.value.find((filter) => filter.type === type);
		const alreadySelected = currentSelection?.values.some((value) => value.id === mappedValue.id);

		let nextFilters: IExtendedSearchQueryFilter[] = [];

		if (selected) {
			if (type !== FilterType.CONTINENT && alreadySelected) {
				return { nextFilters: filtersSelection.value, changed: false, closeContinentOptions: false };
			}

			nextFilters = filtersSelection.value;

			if (type === FilterType.CONTINENT) {
				nextFilters = replaceFilterValues(nextFilters, type, [mappedValue]);
				nextFilters = pruneFiltersForContinent(nextFilters, String(mappedValue.id));
			} else {
				nextFilters = addFilterValue(nextFilters, type, mappedValue);
			}

			if (type === FilterType.AREA) {
				nextFilters = ensureCountrySelection(nextFilters, mappedValue.id);
			}

			return {
				nextFilters,
				changed: true,
				closeContinentOptions: type === FilterType.CONTINENT
			};
		}

		if (!currentSelection) {
			return { nextFilters: filtersSelection.value, changed: false, closeContinentOptions: false };
		}

		nextFilters = type === FilterType.CONTINENT
			? filtersSelection.value.filter((filter) => filter.type !== type)
			: removeFilterValue(filtersSelection.value, type, mappedValue.id);

		if (type === FilterType.COUNTRY) {
			nextFilters = removeAreasForCountry(nextFilters, String(mappedValue.id));
		}

		return { nextFilters, changed: true, closeContinentOptions: false };
	};

	const handleSelect = async (value: string | number, selected: boolean): Promise<void> => {
		const type = valueTypeMap.value.get(value);
		if (!type) return;

		// Strip the prefix to get the actual ID
		const actualId = String(value).replace(/^(country_|area_|continent_)/, '');

		const mapper = type === FilterType.COUNTRY
			? countryMapper
			: type === FilterType.AREA
				? areaMapper
				: continentMapper;

		const mappedValue = mapper.getExtendedFilterValueById(actualId);
		if (!mappedValue) return;

		const { nextFilters, changed, closeContinentOptions } = computeFilterUpdate(type, mappedValue, selected);
		if (!changed) return;

		searchStore.filtersSelection = nextFilters;

		try {
			await searchStore.setRouteAndLoadData();

			if (closeContinentOptions) {
				showContinentOptions.value = false;
			}
		} catch (error) {
			logError('Failed to update location filter', error as Error);
		}
	};

	const clearLocationFilters = async (): Promise<void> => {
		const locationFilterTypes = [FilterType.COUNTRY, FilterType.AREA, FilterType.CONTINENT];
		await searchStore.removeFilterByType(locationFilterTypes);
		closeMobileModal();
	};

	const footer = computed<ModalStateFooter>(() => ({
		actionText: `Add Country`,
		action: (): void => closeMobileModal(),
		actionType: 'secondary',
		clearText: 'Clear all',
		clear: (): void => void clearLocationFilters()
	}));

	return {
		isMobile,
		isContinentOpen,
		showContinentOptions,
		continentItems,
		selectedContinentName: computed(() => selectedContinent.value?.name ?? null),
		selectedIds,
		topCountriesForSelectedContinent,
		multiselectOptionsForSelectedContinent,
		footer,
		toggleContinent,
		handleContinentChange,
		handleSelect
	};
};

export const provideLocationFilterContext = (): LocationFilterContext => {
	const context = createLocationFilterContext();

	provide(locationFilterContextKey, context);

	return context;
};

export const useLocationFilterContext = (): LocationFilterContext => {
	const context = inject(locationFilterContextKey);

	if (!context) {
		throw new Error('useLocationFilterContext must be used within a provider');
	}

	return context;
};
